(function () {
  'use strict';

  const STORAGE_KEY = 'spetool_wishlist_v1';
  const PENDING_COOKIE = 'spetool_wishlist_pending';
  const MIRROR_COOKIE = 'spetool_wishlist_mirror';
  const DIRTY_KEY = 'spetool_wishlist_dirty';
  const SESSION_ADDS_KEY = 'spetool_wishlist_session_adds';
  const SESSION_REMOVES_KEY = 'spetool_wishlist_session_removes';

  const config = window.__WISHLIST_CONFIG__ || {};
  const strings = config.strings || {};
  const maxItems = Number(config.maxItems) || 50;
  const cookieDomain = config.cookieDomain || '';

  let serverAccountIds = new Set();
  let accountRuntimeIds = new Set();

  function normalizeAccountProductIds(raw) {
    if (!raw) return [];
    const list = Array.isArray(raw) ? raw : [];
    return [
      ...new Set(
        list
          .map((entry) => {
            if (typeof entry === 'number') return entry;
            if (typeof entry === 'string') {
              const match = entry.match(/\/Product\/(\d+)/);
              if (match) return Number(match[1]);
              const num = Number(entry);
              return num > 0 ? num : null;
            }
            if (entry && typeof entry === 'object') {
              const id = Number(entry.id);
              return id > 0 ? id : null;
            }
            return null;
          })
          .filter(Boolean),
      ),
    ];
  }

  function usesAccountWishlist() {
    return Boolean(config.loggedIn && config.accountWishlistReady);
  }

  let cachedItems = null;
  let cachedIdSet = null;
  let badgeEls = null;
  let linkEls = null;
  let badgeRaf = 0;

  function invalidateCache() {
    cachedItems = null;
    cachedIdSet = null;
  }

  function rebuildIdSet(items) {
    cachedIdSet = new Set(items.map((item) => Number(item.id)).filter(Boolean));
  }

  function normalizeItems(items) {
    const seen = new Set();
    return (items || [])
      .filter((item) => {
        const id = Number(item?.id);
        if (!Number.isFinite(id) || id <= 0 || seen.has(id)) return false;
        seen.add(id);
        return true;
      })
      .slice(0, maxItems);
  }

  function readLocal() {
    if (cachedItems) return cachedItems;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        cachedItems = [];
        cachedIdSet = new Set();
        return cachedItems;
      }
      const parsed = JSON.parse(raw);
      cachedItems = normalizeItems(Array.isArray(parsed) ? parsed : []);
      rebuildIdSet(cachedItems);
      return cachedItems;
    } catch {
      cachedItems = [];
      cachedIdSet = new Set();
      return cachedItems;
    }
  }

  function writeLocal(items, options) {
    const normalized = normalizeItems(items);
    cachedItems = normalized;
    rebuildIdSet(normalized);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    if (!usesAccountWishlist() && !options?.skipPendingSync) {
      schedulePendingCookieSync();
    }
    return normalized;
  }

  function getWishlistCount() {
    if (usesAccountWishlist()) {
      return getActiveIdSet().size;
    }
    return readLocal().length;
  }

  function localIds() {
    readLocal();
    return Array.from(cachedIdSet);
  }

  function isDirty() {
    try {
      return localStorage.getItem(DIRTY_KEY) === '1';
    } catch {
      return false;
    }
  }

  function setDirty(dirty) {
    try {
      if (dirty) localStorage.setItem(DIRTY_KEY, '1');
      else localStorage.removeItem(DIRTY_KEY);
    } catch {
      /* ignore */
    }
  }

  function readMirrorIds() {
    try {
      const match = document.cookie.match(new RegExp(`${MIRROR_COOKIE}=([^;]+)`));
      if (!match) return null;
      const parsed = JSON.parse(decodeURIComponent(match[1]));
      return Array.isArray(parsed) ? parsed.map(Number).filter(Boolean) : null;
    } catch {
      return null;
    }
  }

  function readSessionAdds() {
    try {
      const raw = sessionStorage.getItem(SESSION_ADDS_KEY);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      return new Set(
        (Array.isArray(parsed) ? parsed : []).map(Number).filter(Boolean),
      );
    } catch {
      return new Set();
    }
  }

  function writeSessionAdds(set) {
    try {
      sessionStorage.setItem(SESSION_ADDS_KEY, JSON.stringify([...set]));
    } catch {
      /* ignore */
    }
  }

  function addSessionAdd(productId) {
    const id = Number(productId);
    if (!id) return;
    const set = readSessionAdds();
    set.add(id);
    writeSessionAdds(set);
  }

  function removeSessionAdd(productId) {
    const id = Number(productId);
    if (!id) return;
    const set = readSessionAdds();
    set.delete(id);
    writeSessionAdds(set);
  }

  function clearSessionAdds() {
    try {
      sessionStorage.removeItem(SESSION_ADDS_KEY);
    } catch {
      /* ignore */
    }
  }

  function readSessionRemoves() {
    try {
      const raw = sessionStorage.getItem(SESSION_REMOVES_KEY);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      return new Set(
        (Array.isArray(parsed) ? parsed : []).map(Number).filter(Boolean),
      );
    } catch {
      return new Set();
    }
  }

  function writeSessionRemoves(set) {
    try {
      sessionStorage.setItem(SESSION_REMOVES_KEY, JSON.stringify([...set]));
    } catch {
      /* ignore */
    }
  }

  function addSessionRemove(productId) {
    const id = Number(productId);
    if (!id) return;
    const set = readSessionRemoves();
    set.add(id);
    writeSessionRemoves(set);
    const adds = readSessionAdds();
    adds.delete(id);
    writeSessionAdds(adds);
  }

  function removeSessionRemove(productId) {
    const id = Number(productId);
    if (!id) return;
    const set = readSessionRemoves();
    set.delete(id);
    writeSessionRemoves(set);
  }

  function clearSessionRemoves() {
    try {
      sessionStorage.removeItem(SESSION_REMOVES_KEY);
    } catch {
      /* ignore */
    }
  }

  function getAuthoritativeMirrorIds() {
    if (!usesAccountWishlist()) return null;
    if (Array.isArray(config.accountProductIds)) {
      return config.accountProductIds.map(Number).filter(Boolean);
    }
    return readMirrorIds();
  }

  function getActiveIdSet() {
    if (usesAccountWishlist()) {
      const set = new Set(accountRuntimeIds);
      readSessionAdds().forEach((id) => set.add(id));
      readSessionRemoves().forEach((id) => set.delete(id));
      return set;
    }
    readLocal();
    return cachedIdSet;
  }

  function bootstrapAccountMode() {
    if (!usesAccountWishlist()) return;

    const ids = normalizeAccountProductIds(config.accountProductIds);
    serverAccountIds = new Set(ids);
    accountRuntimeIds = new Set(ids);

    clearSessionAdds();
    clearSessionRemoves();
    setDirty(false);
    clearPendingCookie();

    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(DIRTY_KEY);
    } catch {
      /* ignore */
    }
    invalidateCache();
  }

  function sortedIdList(ids) {
    return [...new Set(ids.map(Number).filter(Boolean))].sort((a, b) => a - b);
  }

  function idsEqual(a, b) {
    const left = sortedIdList(a);
    const right = sortedIdList(b);
    if (left.length !== right.length) return false;
    return left.every((id, index) => id === right[index]);
  }

  function needsSyncToAccount() {
    if (!usesAccountWishlist()) return false;
    if (readSessionAdds().size > 0) return true;
    if (readSessionRemoves().size > 0) return true;
    return false;
  }

  function applyAccountMirror() {
    if (!usesAccountWishlist()) return;

    const mirrorIds = getAuthoritativeMirrorIds();
    if (mirrorIds === null) return;

    const sessionAdds = readSessionAdds();
    const sessionRemoves = readSessionRemoves();
    const allowed = new Set(mirrorIds);
    sessionAdds.forEach((id) => allowed.add(id));
    sessionRemoves.forEach((id) => allowed.delete(id));

    const items = readLocal();
    const byId = new Map(items.map((item) => [Number(item.id), item]));
    const ordered = [];
    const used = new Set();

    mirrorIds.forEach((id) => {
      if (sessionRemoves.has(id)) return;
      const item = byId.get(id);
      if (!item || used.has(id)) return;
      ordered.push(item);
      used.add(id);
    });

    sessionAdds.forEach((id) => {
      if (used.has(id)) return;
      const item = byId.get(id);
      if (!item) return;
      ordered.push(item);
      used.add(id);
    });

    items.forEach((item) => {
      const id = Number(item.id);
      if (!allowed.has(id) || used.has(id)) return;
      ordered.push(item);
      used.add(id);
    });

    const prevIds = items.map((item) => Number(item.id));
    const nextIds = ordered.map((item) => Number(item.id));
    const changed =
      prevIds.length !== nextIds.length ||
      prevIds.some((id, index) => id !== nextIds[index]);

    if (changed) {
      writeLocal(ordered, {skipPendingSync: !needsSyncToAccount()});
      syncHearts();
      updateCountBadge();
    }

    if (!needsSyncToAccount()) {
      setDirty(false);
      clearSessionAdds();
      clearSessionRemoves();
      clearPendingCookie();
    }
  }

  function reconcileDirtyState() {
    if (!usesAccountWishlist()) return;
    if (!needsSyncToAccount()) {
      setDirty(false);
      clearSessionAdds();
      clearSessionRemoves();
      clearPendingCookie();
    }
  }

  function markAccountDirty() {
    if (!usesAccountWishlist()) return;
    setDirty(true);
    schedulePendingCookieSync();
  }

  let pendingCookieTimer = null;
  function schedulePendingCookieSync() {
    if (!usesAccountWishlist()) return;
    clearTimeout(pendingCookieTimer);
    pendingCookieTimer = setTimeout(syncPendingCookie, 120);
  }

  function syncPendingCookie() {
    if (!usesAccountWishlist()) return;
    if (!needsSyncToAccount()) {
      clearPendingCookie();
      return;
    }
    const ids = localIds();
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    const value = encodeURIComponent(JSON.stringify(ids));
    const base = `${PENDING_COOKIE}=${value}; path=/; max-age=604800; SameSite=Lax${secure}`;
    document.cookie = base;
    if (cookieDomain) {
      document.cookie = `${base}; domain=${cookieDomain}`;
    }
  }

  function buildAccountWishlistSyncUrl() {
    const base = config.accountWishlistUrl;
    if (!base || !base.includes('account.')) return base;
    if (!needsSyncToAccount()) return base;

    const adds = [...readSessionAdds()];
    const removes = [...readSessionRemoves()];
    const params = [];
    if (adds.length) {
      params.push(`wl_add=${encodeURIComponent(JSON.stringify(adds))}`);
    }
    if (removes.length) {
      params.push(`wl_remove=${encodeURIComponent(JSON.stringify(removes))}`);
    }
    if (!params.length) return base;

    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}${params.join('&')}`;
  }

  function clearPendingCookie() {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${PENDING_COOKIE}=; path=/; max-age=0; SameSite=Lax${secure}`;
    if (cookieDomain) {
      document.cookie = `${PENDING_COOKIE}=; path=/; domain=${cookieDomain}; max-age=0; SameSite=Lax${secure}`;
    }
  }

  function hasProduct(productId) {
    return getActiveIdSet().has(Number(productId));
  }

  function setHeartState(btn, active) {
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    btn.setAttribute(
      'aria-label',
      active ? strings.remove || 'Remove from wishlist' : strings.add || 'Add to wishlist',
    );
  }

  function snapshotFromButton(btn) {
    return {
      id: Number(btn.dataset.productId),
      handle: btn.dataset.productHandle || '',
      title: btn.dataset.productTitle || '',
      url: btn.dataset.productUrl || (btn.dataset.productHandle ? `/products/${btn.dataset.productHandle}` : ''),
      image: btn.dataset.productImage || '',
      price: btn.dataset.productPrice || '',
      variant_id: Number(btn.dataset.variantId) || null,
      available: btn.dataset.productAvailable !== 'false',
    };
  }

  function showToast(message) {
    let toast = document.querySelector('[data-wishlist-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'wishlist-toast';
      toast.setAttribute('data-wishlist-toast', '');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(showToast._timer);
    showToast._timer = setTimeout(() => toast.classList.remove('is-visible'), 2800);
  }

  function refreshBadgeCache() {
    badgeEls = document.querySelectorAll('[data-wishlist-count]');
    linkEls = document.querySelectorAll('[data-wishlist-link]');
  }

  function updateCountBadgeNow() {
    if (!badgeEls) refreshBadgeCache();
    const count = getWishlistCount();
    badgeEls.forEach((el) => {
      el.textContent = String(count);
      if (count > 0) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    });
    linkEls.forEach((el) => {
      el.setAttribute('aria-label', count > 0 ? `Wishlist (${count})` : 'Wishlist');
    });
  }

  function updateCountBadge() {
    cancelAnimationFrame(badgeRaf);
    badgeRaf = requestAnimationFrame(updateCountBadgeNow);
  }

  function syncHearts(scope) {
    const idSet = getActiveIdSet();
    const root = scope || document;
    root.querySelectorAll('[data-wishlist-heart]').forEach((btn) => {
      setHeartState(btn, idSet.has(Number(btn.dataset.productId)));
    });
    updateCountBadge();
  }

  function toggleHeart(btn) {
    if (!btn) return;

    const productId = Number(btn.dataset.productId);
    if (!productId) return;

    invalidateCache();

    const active = hasProduct(productId);
    let items = readLocal().slice();

    if (active) {
      items = items.filter((item) => Number(item.id) !== productId);
      setHeartState(btn, false);
      if (usesAccountWishlist()) {
        accountRuntimeIds.delete(productId);
        removeSessionAdd(productId);
        if (serverAccountIds.has(productId)) addSessionRemove(productId);
      }
    } else {
      if (items.length >= maxItems) {
        showToast(strings.limit || 'Wishlist is full (50 items max).');
        return;
      }
      items = [...items, snapshotFromButton(btn)];
      setHeartState(btn, true);
      if (usesAccountWishlist()) {
        accountRuntimeIds.add(productId);
        addSessionAdd(productId);
        removeSessionRemove(productId);
      }
      if (usesAccountWishlist()) {
        showToast(
          strings.addedLoggedIn ||
            'Saved. Open your account wishlist page to sync to your profile.',
        );
      } else if (!config.loggedIn) {
        showToast(strings.addedLocal || 'Added on this device. Sign in to save across devices.');
      }
    }

    writeLocal(items, {skipPendingSync: true});
    updateCountBadge();
    document.dispatchEvent(
      new CustomEvent('wishlist:updated', {detail: {count: getWishlistCount()}}),
    );
  }

  let heartMutationTimer = 0;
  let heartResyncTimer = 0;

  function scheduleHeartResync(scope) {
    clearTimeout(heartResyncTimer);
    heartResyncTimer = setTimeout(() => {
      syncHearts(scope);
      requestAnimationFrame(() => syncHearts(scope));
    }, 60);
  }

  function observeHeartDomChanges() {
    if (!window.MutationObserver) return;

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== 'childList') continue;
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (
            node.matches?.('[data-wishlist-heart]') ||
            node.querySelector?.('[data-wishlist-heart]')
          ) {
            clearTimeout(heartMutationTimer);
            heartMutationTimer = window.setTimeout(() => scheduleHeartResync(), 80);
            return;
          }
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  function bindRevealResync() {
    document.addEventListener(
      'transitionend',
      (event) => {
        const target = event.target;
        if (!target || !target.closest) return;
        if (target.matches('product-item, [data-wishlist-heart]') || target.closest('product-item')) {
          scheduleHeartResync(target.closest('product-item') || undefined);
        }
      },
      true,
    );
  }

  function bindAccountWishlistLink() {
    document.addEventListener(
      'click',
      (event) => {
        const link = event.target.closest('[data-wishlist-link]');
        if (!link || !usesAccountWishlist()) return;
        const base = config.accountWishlistUrl || link.getAttribute('href') || '';
        if (!base || !base.includes('account.')) return;

        if (!needsSyncToAccount()) {
          return;
        }

        event.preventDefault();
        const syncUrl = buildAccountWishlistSyncUrl();
        window.location.href = syncUrl || base;
      },
      true,
    );
  }

  function bindPrefetch() {
    const prefetchPage = (link) => {
      if (!link || link.dataset.wlPrefetched) return;
      const href = link.getAttribute('href') || '';
      if (!href || href.startsWith('/account') || href.includes('account.')) return;
      link.dataset.wlPrefetched = '1';
      const pageLink = document.createElement('link');
      pageLink.rel = 'prefetch';
      pageLink.href = href;
      document.head.appendChild(pageLink);
    };

    document.addEventListener(
      'pointerenter',
      (event) => {
        prefetchPage(event.target.closest('[data-wishlist-link]'));
      },
      { passive: true },
    );
    document.addEventListener(
      'focusin',
      (event) => {
        prefetchPage(event.target.closest('[data-wishlist-link]'));
      },
      { passive: true },
    );
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function getShopifyRoot() {
    return window.Shopify?.routes?.root || window.themeVariables?.routes?.rootUrl || '/';
  }

  function getCartAddUrl() {
    const base = window.themeVariables?.routes?.cartAddUrl || config.cartAddUrl || '/cart/add';
    return base.endsWith('.js') ? base : `${base}.js`;
  }

  function isCartDrawerEnabled() {
    return window.themeVariables?.settings?.cartType === 'drawer';
  }

  async function addToCartFromWishlist(variantId, button) {
    if (!variantId || button.hasAttribute('disabled')) return;

    button.setAttribute('disabled', 'disabled');
    button.setAttribute('aria-busy', 'true');

    try {
      const formData = new FormData();
      formData.append('id', String(variantId));
      formData.append('quantity', '1');
      formData.append('sections', 'mini-cart');
      formData.append('sections_url', window.location.pathname);

      const response = await fetch(getCartAddUrl(), {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      let responseJson = {};
      try {
        responseJson = await response.json();
      } catch {
        responseJson = {};
      }

      if (!response.ok) {
        showToast(responseJson.description || responseJson.message || strings.addToCartError || 'Could not add to cart.');
        return;
      }

      if (!isCartDrawerEnabled() || window.themeVariables?.settings?.pageType === 'cart') {
        window.location.href = `${getShopifyRoot()}cart`;
        return;
      }

      const cartResponse = await fetch(`${getShopifyRoot()}cart.js`);
      const cartContent = await cartResponse.json();
      cartContent.sections = responseJson.sections;

      const addedVariant = responseJson.hasOwnProperty('items') ? responseJson.items[0] : responseJson;
      const cartDrawer = document.querySelector('cart-drawer#mini-cart');
      if (cartDrawer) {
        cartDrawer.dispatchEvent(
          new CustomEvent('variant:added', {
            bubbles: true,
            detail: { variant: addedVariant },
          }),
        );
      }

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart: cartContent },
        }),
      );
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart: cartContent,
            openMiniCart: true,
          },
        }),
      );
    } catch {
      showToast(strings.addToCartError || 'Could not add to cart.');
    } finally {
      button.removeAttribute('disabled');
      button.removeAttribute('aria-busy');
    }
  }

  function renderCard(product, index) {
    const id = Number(product.id);
    const url = product.url || (product.handle ? `/products/${product.handle}` : '#');
    const image = product.image || '';
    const title = product.title || '';
    const price = product.price || '';
    const available = product.available !== false;
    const variantId = Number(product.variant_id);
    const canAddToCart = available && Number.isFinite(variantId) && variantId > 0;
    const imgLoading = index < 6 ? 'eager' : 'lazy';
    const imgFetch = index < 6 ? 'fetchpriority="high"' : '';

    const card = document.createElement('article');
    card.className = 'wishlist-card';
    card.dataset.wishlistCard = String(id);
    card.innerHTML = `
      <div class="wishlist-card__media">
        <a href="${escapeHtml(url)}">
          ${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(title)}" loading="${imgLoading}" ${imgFetch} decoding="async" width="400" height="400">` : ''}
        </a>
      </div>
      <div class="wishlist-card__body">
        <h3 class="wishlist-card__title"><a href="${escapeHtml(url)}">${escapeHtml(title)}</a></h3>
        ${price ? `<div class="wishlist-card__price">${escapeHtml(price)}</div>` : ''}
        <div class="wishlist-card__status">${available ? '&nbsp;' : escapeHtml(strings.soldOut || 'Sold out')}</div>
      </div>
      <div class="wishlist-card__actions">
        <button type="button" class="button button--secondary button--full wishlist-card__btn" data-wishlist-remove="${id}">${escapeHtml(strings.removeItem || 'Remove')}</button>
        ${
          canAddToCart
            ? `<button type="button" class="button button--primary button--full wishlist-card__btn" data-wishlist-atc="${variantId}">${escapeHtml(strings.addToCart || 'Add to cart')}</button>`
            : `<a href="${escapeHtml(url)}" class="button button--primary button--full wishlist-card__btn">${escapeHtml(strings.viewProduct || 'View product')}</a>`
        }
      </div>
    `;
    return card;
  }

  function syncWishlistPageBanners(root) {
    const guestBanner = root.querySelector('[data-wishlist-banner]');
    const loggedInBanner = root.querySelector('[data-wishlist-logged-in-banner]');

    if (usesAccountWishlist()) {
      if (guestBanner) guestBanner.hidden = true;
      if (loggedInBanner) loggedInBanner.hidden = false;
      return;
    }

    if (guestBanner) guestBanner.hidden = Boolean(config.loggedIn);
    if (loggedInBanner) loggedInBanner.hidden = true;
  }

  function renderGuestWishlistPage(root) {
    const loading = root.querySelector('[data-wishlist-loading]');
    const empty = root.querySelector('[data-wishlist-empty]');
    const grid = root.querySelector('[data-wishlist-grid]');

    if (loading) loading.hidden = true;
    syncWishlistPageBanners(root);

    const products = readLocal();

    if (empty) empty.hidden = products.length > 0;
    if (grid) {
      grid.replaceChildren();
      grid.hidden = products.length === 0;
      if (products.length) {
        const fragment = document.createDocumentFragment();
        products.forEach((product, index) => fragment.appendChild(renderCard(product, index)));
        grid.appendChild(fragment);
      }
    }

    root.dataset.wlReady = 'true';
    updateCountBadge();
  }

  function initWishlistPage() {
    const root = document.querySelector('[data-wishlist-page]');
    if (!root || root.dataset.wlInitialized) return;
    root.dataset.wlInitialized = 'true';

    if (usesAccountWishlist()) {
      const loading = root.querySelector('[data-wishlist-loading]');
      const empty = root.querySelector('[data-wishlist-empty]');
      const grid = root.querySelector('[data-wishlist-grid]');

      if (loading) loading.hidden = true;
      syncWishlistPageBanners(root);
      if (empty) empty.hidden = true;
      if (grid) grid.hidden = true;

      if (needsSyncToAccount()) syncPendingCookie();
      else clearPendingCookie();
      updateCountBadge();
      root.dataset.wlReady = 'true';
      return;
    }

    renderGuestWishlistPage(root);

    root.addEventListener('click', (event) => {
      const atcBtn = event.target.closest('[data-wishlist-atc]');
      if (atcBtn) {
        event.preventDefault();
        addToCartFromWishlist(Number(atcBtn.dataset.wishlistAtc), atcBtn);
        return;
      }

      const removeBtn = event.target.closest('[data-wishlist-remove]');
      if (!removeBtn) return;
      event.preventDefault();
      const productId = Number(removeBtn.dataset.wishlistRemove);
      if (!productId) return;

      writeLocal(readLocal().filter((item) => Number(item.id) !== productId));

      const card = root.querySelector(`[data-wishlist-card="${productId}"]`);
      if (card) card.remove();
      updateCountBadge();

      const grid = root.querySelector('[data-wishlist-grid]');
      if (grid && !grid.children.length) {
        const empty = root.querySelector('[data-wishlist-empty]');
        if (empty) empty.hidden = false;
        grid.hidden = true;
      }

      document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { count: getWishlistCount() } }));
    });
  }

  function normalizeStorageOnce() {
    const items = readLocal();
    const serialized = JSON.stringify(items);
    if (localStorage.getItem(STORAGE_KEY) !== serialized) {
      localStorage.setItem(STORAGE_KEY, serialized);
      schedulePendingCookieSync();
    }
  }

  function init() {
    if (window.__WL_INIT__) return;
    window.__WL_INIT__ = true;

    window.__WL_HANDLE_HEART__ = toggleHeart;
    window.__WL_SYNC_PENDING_COOKIE__ = syncPendingCookie;
    window.__WL_CLEAR_PENDING_COOKIE__ = clearPendingCookie;
    window.__WL_RESYNC_HEARTS__ = () => syncHearts();

    document.addEventListener('wishlist:updated', () => {
      scheduleHeartResync();
    });

    bindAccountWishlistLink();
    bindPrefetch();
    observeHeartDomChanges();
    bindRevealResync();
    invalidateCache();
    refreshBadgeCache();
    bootstrapAccountMode();
    if (usesAccountWishlist()) {
      reconcileDirtyState();
    }
    readLocal();
    syncHearts();
    scheduleHeartResync();
    [250, 800, 2000].forEach((ms) => window.setTimeout(() => syncHearts(), ms));

    const wishlistRoot = document.querySelector('[data-wishlist-page]');
    if (wishlistRoot) {
      initWishlistPage();
    }

    if ('requestIdleCallback' in window) {
      requestIdleCallback(
        () => {
          if (!usesAccountWishlist()) normalizeStorageOnce();
        },
        { timeout: 800 },
      );
    } else {
      setTimeout(() => {
        if (!usesAccountWishlist()) normalizeStorageOnce();
      }, 0);
    }

    window.addEventListener('storage', (event) => {
      if (event.key !== STORAGE_KEY) return;
      invalidateCache();
      syncHearts();
      const root = document.querySelector('[data-wishlist-page]');
      if (root && !usesAccountWishlist()) {
        renderGuestWishlistPage(root);
      }
    });

    document.addEventListener('shopify:section:load', () => {
      refreshBadgeCache();
      scheduleHeartResync();
    });

    window.addEventListener('pageshow', () => {
      if (usesAccountWishlist() && /account\./i.test(document.referrer || '')) {
        window.location.reload();
        return;
      }

      invalidateCache();
      refreshBadgeCache();
      bootstrapAccountMode();
      if (usesAccountWishlist()) {
        reconcileDirtyState();
      }
      updateCountBadgeNow();
      syncHearts();
      const root = document.querySelector('[data-wishlist-page]');
      if (root && !usesAccountWishlist()) {
        renderGuestWishlistPage(root);
      }
    });
  }

  function boot() {
    init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
